// src/components/common/Header.tsx
import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ThemeToggle from '../ui/ThemeToggle';
import LanguageSwitcher from './LanguageSwitcher';
import { useTranslation } from 'react-i18next';
import { CurrencyYenIcon, HomeIcon, SparklesIcon, Cog6ToothIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import logoImage from '../../assets/images/UI_orifoli_logo_jp.png';
import logoImageWhite from '../../assets/images/UI_orifoli_logo_white_jp.png';
import logoImageBlackMobile from '../../assets/images/UI_orifoli_logo_black.png';
import logoImageWhiteMobile from '../../assets/images/UI_orifoli_logo_white.png';
import figryneLogoIcon from '../../assets/images/UI_Figryne_new_logo_icon.png';
import { config } from '../../config';

interface HeaderProps {
  onCreateClick?: () => void;
  onLoginClick?: () => void;
  onSignupClick?: () => void;
}

const Header: React.FC<HeaderProps> = ({ onCreateClick, onLoginClick, onSignupClick }) => {
  const { user, signOut } = useAuth();
  const { t } = useTranslation('common');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // メニュー外クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSignOut = async () => {
    await signOut();
    setIsMenuOpen(false);
    navigate('/');
  };

  return (
    <header className="relative z-50 px-4 py-3 flex justify-between items-center bg-white dark:bg-gray-900 transition-colors">
      {/* Logo */}
      <div className="flex items-center">
        {/* Figryne logo - hidden on mobile, links to figryne.com */}
        <a href={config.figryneUrl} className="hidden sm:block">
          <img src={figryneLogoIcon} alt="Figryne" className="h-6 hover:opacity-90 transition-opacity invert dark:invert-0" />
        </a>
        <ChevronRightIcon className="hidden sm:block w-4 h-4 text-gray-400 dark:text-gray-300 mx-1" />
        {/* ORIFOLI logo */}
        <Link to="/">
          {/* Desktop Light: JP logo */}
          <img src={logoImage} alt="ORIFOLI" className="h-6 hidden sm:block dark:hidden hover:opacity-80 transition-opacity" />
          {/* Desktop Dark: JP white logo */}
          <img src={logoImageWhite} alt="ORIFOLI" className="h-6 hidden dark:sm:block hover:opacity-80 transition-opacity" />
          {/* Mobile Light: Black logo */}
          <img src={logoImageBlackMobile} alt="ORIFOLI" className="h-6 block sm:hidden dark:hidden hover:opacity-80 transition-opacity" />
          {/* Mobile Dark: White logo */}
          <img src={logoImageWhiteMobile} alt="ORIFOLI" className="h-6 hidden dark:block dark:sm:hidden hover:opacity-80 transition-opacity" />
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex items-center gap-1">
        {/* Language Switcher */}
        <LanguageSwitcher />
        {/* Theme Toggle */}
        <ThemeToggle />
        {user ? (
          <div className="flex items-center space-x-4">
            {/* Create Button */}
            <button
              onClick={onCreateClick}
              className="hidden sm:block bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-3 py-1.5 rounded-full text-xs font-medium hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
            >
              Create
            </button>

            {/* User Menu */}
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="flex items-center"
              >
                {user.photoURL ? (
                  <img
                    src={user.photoURL}
                    alt="Avatar"
                    className="w-7 h-7 rounded-full shadow-sm hover:shadow-md transition-all duration-200 object-cover"
                  />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-xs font-bold shadow-sm hover:shadow-md transition-all duration-200">
                    {user.displayName?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase() || '?'}
                  </div>
                )}
              </button>

              {/* Dropdown Menu */}
              {isMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg py-1 z-[100] border border-gray-100 dark:border-gray-700">
                  {/* User Info */}
                  <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {user.displayName || 'ユーザー'}
                    </p>
                    {user.email && (
                      <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate">
                        @{user.email.split('@')[0]}
                      </p>
                    )}
                  </div>
                  <Link
                    to="/my-characters"
                    className="flex items-center gap-2 px-3 py-2 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <SparklesIcon className="w-4 h-4 text-yellow-500" />
                    {t('navigation.myCharacters')}
                  </Link>
                  <Link
                    to="/adopt"
                    className="flex items-center gap-2 px-3 py-2 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <HomeIcon className="w-4 h-4 text-pink-500" />
                    {t('navigation.adopt')}
                  </Link>
                  <Link
                    to="/earnings"
                    className="flex items-center gap-2 px-3 py-2 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <CurrencyYenIcon className="w-4 h-4 text-purple-500" />
                    {t('navigation.earnings')}
                  </Link>
                  <Link
                    to="/settings"
                    className="flex items-center gap-2 px-3 py-2 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <Cog6ToothIcon className="w-4 h-4 text-gray-500" />
                    {t('navigation.settings')}
                  </Link>
                  {onCreateClick && (
                    <button
                      onClick={() => {
                        setIsMenuOpen(false);
                        onCreateClick();
                      }}
                      className="block w-full text-left px-3 py-2 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 sm:hidden"
                    >
                      {t('navigation.createCharacter')}
                    </button>
                  )}
                  <hr className="my-1 border-gray-100 dark:border-gray-700" />
                  <button
                    onClick={handleSignOut}
                    className="block w-full text-left px-3 py-2 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    {t('navigation.logout')}
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <button
              onClick={onLoginClick}
              className="px-3 text-xs sm:px-4 py-2 bg-[#B566FF] hover:bg-[#CB92FF] text-white font-bold rounded-full transition-colors"
            >
              {t('navigation.login')}
            </button>
            <button
              onClick={onSignupClick}
              className="hidden sm:block px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-full font-medium text-xs hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
            >
              {t('navigation.signup')}
            </button>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
